/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */
import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { User, Building2, Search } from 'lucide-react';
import { sessioniAPI } from '../api/sessioni';
import { utentiAPI } from '../api/utenti';
import { useViewport } from '../hooks/useViewport';
import { useFineSessione } from '../hooks/useFineSessione';
import { fmtDataOra } from '../utils/date';
import { fmtDurata } from '../utils/durata';

const ACCENT = '#10B981';
const DICHIARAZIONE_LEGALE = "Consapevole delle sanzioni penali e della decadenza dai benefici nel caso di dichiarazioni non veritiere, di formazione o uso di atti falsi, richiamate dall'art. 76 del D.P.R. n. 445/2000, dichiaro che le informazioni fornite sono corrette e mi assumo la responsabilità della loro veridicità.";

const CATEGORIE_INTERNE = {
  magazzino: 'Magazzino',
  manutenzione_automezzi: 'Manutenzione automezzi',
  formazione: 'Formazione',
  riunione: 'Riunione',
  altro: 'Altro',
};

// Modale mostrato alla chiusura di una sessione "libera" (senza intervento assegnato):
// il tecnico sceglie se il lavoro va associato a un cliente (crea un nuovo intervento)
// oppure se è lavoro interno per l'azienda (magazzino, formazione, ecc.)
export default function AssegnaLavoroModal({ sessione, onClose, onDone, setMsg }) {
  const { isMobile } = useViewport();
  const [tab, setTab] = useState('cliente');

  // Destinazione: cliente
  const [q, setQ] = useState('');
  const [cliente, setCliente] = useState(null);
  const [titoloCliente, setTitoloCliente] = useState('');
  const [descrizione, setDescrizione] = useState('');

  // Destinazione: lavoro interno
  const [categoria, setCategoria] = useState('magazzino');
  const [titoloInterno, setTitoloInterno] = useState('');

  // Campi comuni (come lo stop di una sessione normale)
  const f = useFineSessione(sessione.inizio);
  const [dichiarazione, setDichiarazione] = useState('');
  const [note, setNote] = useState('');
  const [accettato, setAccettato] = useState(false);
  const [err, setErr] = useState('');

  const { data: clienti = [] } = useQuery({
    queryKey: ['clienti-search', q],
    queryFn: () => utentiAPI.listClienti(q || undefined),
    enabled: tab === 'cliente' && !cliente,
  });

  const dichOk = dichiarazione.trim().length > 0;
  const destinazioneOk = tab === 'cliente'
    ? !!cliente && titoloCliente.trim().length > 0
    : !!categoria && (categoria !== 'altro' || titoloInterno.trim().length > 0);

  const stopMut = useMutation({
    mutationFn: () => {
      const base = {
        note: note || null,
        pausa_minuti: f.minutiPausa,
        fine: f.fine.toISOString(),
        dichiarazione,
        dichiarazione_accettata: accettato,
      };
      if (tab === 'cliente') {
        return sessioniAPI.stop(sessione.id, { ...base, cliente_id: cliente.id, titolo: titoloCliente.trim(), descrizione: descrizione.trim() || undefined });
      }
      return sessioniAPI.stop(sessione.id, { ...base, interno: true, categoria_interna: categoria, titolo: titoloInterno.trim() || CATEGORIE_INTERNE[categoria] });
    },
    onSuccess: onDone,
    onError: (e) => setErr(e.response?.data?.error || 'Errore'),
  });

  const puoConfermare = dichOk && accettato && destinazioneOk && f.puoSalvare && !stopMut.isPending;

  return (
    <div style={{ ...styles.overlay, ...(isMobile ? styles.overlayMobile : {}) }} onClick={onClose}>
      <div style={{ ...styles.modal, ...(isMobile ? styles.modalMobile : {}) }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Chiudi lavoro libero</h3>

        <div style={styles.tabs}>
          <button onClick={() => setTab('cliente')} style={{ ...styles.tabBtn, ...(tab === 'cliente' ? styles.tabBtnActive : {}) }}>
            <User size={15} /> Cliente
          </button>
          <button onClick={() => setTab('interno')} style={{ ...styles.tabBtn, ...(tab === 'interno' ? styles.tabBtnActive : {}) }}>
            <Building2 size={15} /> Lavoro interno
          </button>
        </div>

        {tab === 'cliente' ? (
          !cliente ? (
            <div style={styles.field}>
              <label style={styles.lbl}>Cerca cliente *</label>
              <div style={styles.searchWrap}>
                <Search size={15} style={styles.searchIcon} />
                <input value={q} onChange={(e) => setQ(e.target.value)} style={styles.searchInput} placeholder="Nome, cognome..." autoFocus />
              </div>
              <div style={styles.searchList}>
                {clienti.map((c) => (
                  <div key={c.id} style={styles.searchItem} onClick={() => setCliente(c)}>
                    <div style={{ fontWeight: 600 }}>{c.nome} {c.cognome}</div>
                    {c.telefono && <div style={styles.rowMeta}>{c.telefono}</div>}
                  </div>
                ))}
                {clienti.length === 0 && <div style={styles.empty}>Nessun cliente trovato.</div>}
              </div>
            </div>
          ) : (
            <>
              <div style={styles.selBox}>
                <div style={{ fontWeight: 600 }}>{cliente.nome} {cliente.cognome}</div>
                <button onClick={() => setCliente(null)} style={styles.changeBtn}>Cambia</button>
              </div>
              <div style={styles.field}>
                <label style={styles.lbl}>Titolo intervento *</label>
                <input value={titoloCliente} onChange={(e) => setTitoloCliente(e.target.value)} style={styles.input} placeholder="es. Sostituzione valvola caldaia" autoFocus />
              </div>
              <div style={styles.field}>
                <label style={styles.lbl}>Descrizione (opzionale)</label>
                <textarea value={descrizione} onChange={(e) => setDescrizione(e.target.value)} style={{ ...styles.input, minHeight: '50px', fontFamily: 'inherit' }} />
              </div>
            </>
          )
        ) : (
          <>
            <div style={styles.field}>
              <label style={styles.lbl}>Categoria *</label>
              <select value={categoria} onChange={(e) => setCategoria(e.target.value)} style={styles.input}>
                {Object.entries(CATEGORIE_INTERNE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div style={styles.field}>
              <label style={styles.lbl}>Titolo {categoria === 'altro' ? '*' : '(opzionale)'}</label>
              <input value={titoloInterno} onChange={(e) => setTitoloInterno(e.target.value)} style={styles.input} placeholder={categoria === 'altro' ? 'Descrivi il lavoro svolto' : CATEGORIE_INTERNE[categoria]} />
            </div>
          </>
        )}

        {f.oltreUnGiorno && (
          <div style={styles.avvisoLungo}>
            Questo timer gira da <strong>{fmtDurata(f.durataTimer)}</strong>. Controlla data e ora di fine:
            verranno conteggiate solo le ore che indichi qui.
          </div>
        )}
        <div style={styles.field}>
          <label style={styles.lbl}>Inizio</label>
          <div style={styles.readonly}>{fmtDataOra(sessione.inizio)}</div>
        </div>
        {f.mostraData && (
          <div style={styles.field}>
            <label style={styles.lbl}>Data fine</label>
            <input type="date" value={f.fineData} onChange={(e) => f.setFineData(e.target.value)} style={styles.input} />
          </div>
        )}
        <div style={styles.field}>
          <label style={styles.lbl}>Ora fine (modificabile)</label>
          <input type="time" value={f.fineOra} onChange={(e) => f.setFineOra(e.target.value)} style={styles.input} />
        </div>
        <div style={styles.field}>
          <label style={styles.lbl}>Pausa (minuti)</label>
          <input type="number" min="0" value={f.pausa} onChange={(e) => f.setPausa(e.target.value)} style={styles.input} />
        </div>
        <div style={styles.field}>
          <label style={styles.lbl}>Dichiara lavoro svolto *</label>
          <textarea value={dichiarazione} onChange={(e) => setDichiarazione(e.target.value)} style={{ ...styles.input, minHeight: '60px', fontFamily: 'inherit' }} placeholder="Cosa hai fatto in questa sessione..." />
        </div>
        <div style={styles.field}>
          <label style={styles.lbl}>Note (opzionale)</label>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} style={{ ...styles.input, minHeight: '45px', fontFamily: 'inherit' }} />
        </div>
        <div style={styles.oreStimate}>Ore conteggiate: <strong>{fmtDurata(f.secondiStimati)}</strong></div>
        <label style={styles.checkboxRow}>
          <input type="checkbox" checked={accettato} onChange={(e) => setAccettato(e.target.checked)} style={{ width: '18px', height: '18px', flexShrink: 0, marginTop: '2px' }} />
          <span style={styles.checkboxText}>{DICHIARAZIONE_LEGALE}</span>
        </label>
        {f.primaDellInizio && <div style={styles.errBox}>⚠️ La fine deve essere successiva all'inizio. Controlla la data.</div>}
        {f.nelFuturo && <div style={styles.errBox}>⚠️ La fine è nel futuro. Correggila per continuare.</div>}
        {err && <div style={styles.errBox}>{err}</div>}
        <div style={{ ...styles.modalActions, ...(isMobile ? styles.modalActionsMobile : {}) }}>
          <button onClick={onClose} style={{ ...styles.cancelBtn, ...(isMobile ? styles.modalBtnMobile : {}) }}>Annulla</button>
          <button onClick={() => stopMut.mutate()} disabled={!puoConfermare} style={{ ...styles.confirmBtn, ...(isMobile ? styles.modalBtnMobile : {}), opacity: puoConfermare ? 1 : 0.5 }}>
            {stopMut.isPending ? 'Salvo...' : 'Conferma'}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem' },
  overlayMobile: { padding: 0, alignItems: 'flex-end' },
  modal: { backgroundColor: 'white', borderRadius: '14px', padding: '1.5rem', width: '100%', maxWidth: '440px', maxHeight: '90vh', overflow: 'auto' },
  modalMobile: { borderRadius: '16px 16px 0 0', padding: '1.25rem', maxWidth: '100%', maxHeight: '92vh' },
  tabs: { display: 'flex', gap: '0.5rem', marginBottom: '1rem' },
  tabBtn: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', padding: '0.6rem', backgroundColor: '#F3F4F6', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem', color: '#4B5563' },
  tabBtnActive: { backgroundColor: ACCENT, color: 'white' },
  field: { display: 'flex', flexDirection: 'column', gap: '0.3rem', marginBottom: '0.85rem' },
  lbl: { fontSize: '0.82rem', fontWeight: 600, color: '#374151' },
  input: { padding: '0.6rem 0.8rem', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', width: '100%' },
  readonly: { padding: '0.6rem 0.8rem', backgroundColor: '#F3F4F6', borderRadius: '8px', fontSize: '0.95rem', color: '#6B7280' },
  searchWrap: { position: 'relative' },
  searchIcon: { position: 'absolute', left: '0.7rem', top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' },
  searchInput: { padding: '0.6rem 0.8rem 0.6rem 2.1rem', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', width: '100%' },
  searchList: { display: 'flex', flexDirection: 'column', gap: '0.4rem', maxHeight: '200px', overflow: 'auto', marginTop: '0.6rem' },
  searchItem: { padding: '0.7rem 0.85rem', backgroundColor: '#F9FAFB', borderRadius: '8px', cursor: 'pointer' },
  rowMeta: { fontSize: '0.8rem', color: '#6B7280', marginTop: '0.15rem' },
  empty: { padding: '1rem', textAlign: 'center', color: '#9CA3AF', fontSize: '0.85rem' },
  selBox: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 0.85rem', backgroundColor: '#F0FDF4', borderRadius: '8px', marginBottom: '0.85rem' },
  changeBtn: { background: 'none', border: 'none', color: ACCENT, fontWeight: 600, cursor: 'pointer', fontSize: '0.82rem' },
  avvisoLungo: { padding: '0.8rem', backgroundColor: '#FEF3C7', color: '#92400E', borderRadius: '10px', fontSize: '0.85rem', lineHeight: 1.45, marginBottom: '0.9rem' },
  oreStimate: { padding: '0.7rem', backgroundColor: '#F0FDF4', borderRadius: '8px', textAlign: 'center', fontSize: '0.95rem', marginBottom: '0.85rem' },
  checkboxRow: { display: 'flex', alignItems: 'flex-start', gap: '0.6rem', padding: '0.75rem', backgroundColor: '#F9FAFB', borderRadius: '8px', marginBottom: '0.85rem', cursor: 'pointer' },
  checkboxText: { fontSize: '0.78rem', color: '#4B5563', lineHeight: 1.4 },
  errBox: { backgroundColor: '#FEE2E2', color: '#991B1B', padding: '0.65rem', borderRadius: '8px', fontSize: '0.85rem', marginBottom: '0.85rem' },
  modalActions: { display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' },
  modalActionsMobile: { gap: '0.6rem' },
  modalBtnMobile: { flex: 1, padding: '0.85rem', fontSize: '0.95rem', textAlign: 'center', justifyContent: 'center' },
  cancelBtn: { padding: '0.6rem 1.1rem', backgroundColor: 'white', color: '#374151', border: '1px solid #D1D5DB', borderRadius: '8px', cursor: 'pointer', fontWeight: 500 },
  confirmBtn: { padding: '0.6rem 1.2rem', backgroundColor: ACCENT, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 },
};
